import React from 'react';
import { Navigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import AuthContext from '../contexts/AuthContext';
import { Input, Button, useToast } from '../design-system';

// Mirror of RegisterPage.js / ResetPasswordPage.js password strength rules.
// Kept in sync with server/src/core/passwordRules.js — change both together.
const BLOCKED_PASSWORDS = [
  'password', 'password1', 'password12', 'password123', 'password1234',
  'qwerty', 'qwerty123', 'letmein', 'welcome', 'welcome1',
  'abc123', 'abcdef', '123456', '1234567', '12345678', '123456789',
  'admin', 'admin123', 'administrator', 'login', 'master',
  'monkey', 'dragon', 'football', 'baseball', 'soccer',
  'iloveyou', 'trustno1', 'sunshine', 'princess', 'shadow',
  'superman', 'michael', 'charlie', 'donald', 'access',
  'hello', 'passw0rd', 'p@ssword', 'p@ssw0rd',
  'changeme', 'test', 'test123', 'guest', 'default',
  'inspection', 'inspector', 'inspector1', 'inspector123',
  'butler', 'butler123', 'septic', 'septic123',
  'spectora', 'homegauge', 'tanktrak'
];

function getPasswordRules(password) {
  return [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'One uppercase letter (A-Z)', met: /[A-Z]/.test(password) },
    { label: 'One lowercase letter (a-z)', met: /[a-z]/.test(password) },
    { label: 'One number (0-9)', met: /[0-9]/.test(password) },
    { label: 'One special character', met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password) },
    { label: 'Not a commonly used password', met: password.length > 0 && !BLOCKED_PASSWORDS.includes(password.toLowerCase()) }
  ];
}

function PasswordStrength({ password }) {
  const rules = getPasswordRules(password);
  const metCount = rules.filter(r => r.met).length;
  const allMet = metCount === rules.length;
  const started = password.length > 0;
  if (!started) return null;
  const pct = Math.round((metCount / rules.length) * 100);
  const barColor = pct < 50 ? 'var(--color-error)' : pct < 100 ? 'var(--color-warning)' : 'var(--color-success)';
  const label = pct < 50 ? 'Weak' : pct < 100 ? 'Getting there' : 'Strong';

  return (
    <div style={{
      marginTop: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)',
      backgroundColor: allMet ? 'var(--color-success-50)' : 'var(--surface-sunken)',
      borderRadius: 'var(--radius-lg)',
      border: allMet ? '1px solid var(--color-success-200)' : '1px solid var(--border-default)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
        <div style={{ flex: 1, height: 4, backgroundColor: 'var(--border-default)', borderRadius: 'var(--radius-full)' }}>
          <div style={{ width: `${pct}%`, height: '100%', backgroundColor: barColor, borderRadius: 'var(--radius-full)', transition: 'width 0.3s' }} />
        </div>
        <span style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-semibold)', color: barColor }}>{label}</span>
      </div>
      {rules.map((rule, i) => (
        <div key={i} style={{
          fontSize: 'var(--text-caption)', color: rule.met ? 'var(--color-success-600)' : 'var(--text-tertiary)',
          display: 'flex', alignItems: 'center', gap: 'var(--space-1_5)', marginBottom: 'var(--space-0_5)',
          fontFamily: 'var(--font-family)',
        }}>
          <span style={{ fontSize: 'var(--text-body-sm)', width: '16px', textAlign: 'center' }}>{rule.met ? '✓' : '•'}</span>
          <span style={{ textDecoration: rule.met ? 'line-through' : 'none' }}>{rule.label}</span>
        </div>
      ))}
    </div>
  );
}

function SettingsPage() {
  const auth = React.useContext(AuthContext);
  const toast = useToast();
  const { theme, updateTheme, resetTheme } = useTheme();
  const [saved, setSaved] = React.useState(false);

  // Manager unlock code state
  const [unlockStatus, setUnlockStatus] = React.useState({ hasCode: false, label: null, lastUsedAt: null, updatedAt: null });
  const [codeInput, setCodeInput] = React.useState('');
  const [codeConfirm, setCodeConfirm] = React.useState('');
  const [codeLabel, setCodeLabel] = React.useState('');
  const [codeSaving, setCodeSaving] = React.useState(false);
  const [codeMsg, setCodeMsg] = React.useState(null);

  // Change password state
  const [pwCurrent, setPwCurrent] = React.useState('');
  const [pwNew, setPwNew] = React.useState('');
  const [pwConfirm, setPwConfirm] = React.useState('');
  const [pwSaving, setPwSaving] = React.useState(false);

  // Face ID / passkey devices — checked FRESH from the server every time this
  // page opens, so there is no stored "done" flag that can go stale (the hub
  // button that did have one is removed).
  const pkSupported = typeof auth.passkeySupported === 'function' ? auth.passkeySupported() : false;
  const [pkDevices, setPkDevices] = React.useState([]);
  const [pkListLoading, setPkListLoading] = React.useState(false);
  const [pkBusy, setPkBusy] = React.useState(false);
  const refreshPasskeys = React.useCallback(async () => {
    if (!pkSupported || typeof auth.listPasskeys !== 'function') return;
    setPkListLoading(true);
    try { setPkDevices(await auth.listPasskeys()); }
    catch (err) { /* leave the list as-is; a load failure shouldn't nuke the page */ }
    finally { setPkListLoading(false); }
  }, [auth, pkSupported]);
  React.useEffect(() => { refreshPasskeys(); }, [refreshPasskeys]);

  const handleSetupPasskey = async () => {
    setPkBusy(true);
    try {
      const r = await auth.registerPasskey();
      toast.success(r && r.alreadyRegistered ? 'This device was already set up.' : 'Face ID is set up on this device.');
      await refreshPasskeys();
    } catch (err) {
      const name = err && err.name;
      const msg = (err && err.message) || '';
      if (name === 'NotAllowedError' || name === 'AbortError' || /cancel|abort/i.test(msg)) { /* user dismissed — stay quiet */ }
      else { toast.error(msg || 'Could not set up Face ID on this device.'); }
    } finally { setPkBusy(false); }
  };

  const handleRemovePasskey = async (id, label) => {
    if (!window.confirm('Remove ' + (label || 'this device') + '? You will no longer be able to sign in with Face ID from it until you set it up again.')) return;
    setPkBusy(true);
    try {
      await auth.removePasskey(id);
      toast.success('Removed.');
      await refreshPasskeys();
    } catch (err) {
      toast.error((err && err.message) || 'Could not remove that device.');
    } finally { setPkBusy(false); }
  };

  const fmtPkDate = (d) => { try { return d ? new Date(d).toLocaleDateString() : null; } catch (_) { return null; } };

  const pwNewValid = getPasswordRules(pwNew).every(r => r.met);
  const pwMatches = pwNew.length > 0 && pwNew === pwConfirm;
  const pwDifferent = pwCurrent.length > 0 && pwNew.length > 0 && pwCurrent !== pwNew;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwCurrent) { toast.error('Current password is required'); return; }
    if (!pwNew) { toast.error('New password is required'); return; }
    if (!pwNewValid) { toast.error('New password does not meet the requirements below.'); return; }
    if (pwCurrent === pwNew) { toast.error('New password must be different from current password'); return; }
    if (pwNew !== pwConfirm) { toast.error('New password and confirmation do not match'); return; }

    setPwSaving(true);
    try {
      const res = await auth.authFetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: pwCurrent, newPassword: pwNew })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Password change failed');
      }
      toast.success('Password changed');
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (err) {
      toast.error(err.message || 'Password change failed');
    } finally {
      setPwSaving(false);
    }
  };

  React.useEffect(() => {
    if (!auth.isAuthenticated) return;
    auth.authFetch('/api/review/unlock-code/status')
      .then(r => r.json())
      .then(j => { if (j && j.data) setUnlockStatus(j.data); })
      .catch(() => {});
  }, [auth]);

  const saveUnlockCode = async () => {
    setCodeMsg(null);
    if (codeInput.length < 4) { setCodeMsg({ type: 'error', text: 'Code must be at least 4 characters.' }); return; }
    if (codeInput !== codeConfirm) { setCodeMsg({ type: 'error', text: 'Codes do not match.' }); return; }
    setCodeSaving(true);
    try {
      const res = await auth.authFetch('/api/review/unlock-code', {
        method: 'POST',
        body: JSON.stringify({ code: codeInput, label: codeLabel || null })
      });
      const j = await res.json();
      if (!res.ok) throw new Error((j.error && j.error.message) || 'Save failed');
      setCodeMsg({ type: 'success', text: unlockStatus.hasCode ? 'Code rotated.' : 'Code set.' });
      setUnlockStatus({ hasCode: true, label: codeLabel || null, lastUsedAt: null, updatedAt: new Date().toISOString() });
      setCodeInput(''); setCodeConfirm(''); setCodeLabel('');
    } catch (err) {
      setCodeMsg({ type: 'error', text: err.message });
    } finally {
      setCodeSaving(false);
    }
  };

  if (!auth.isAuthenticated) return <Navigate to="/login" />;

  const handleColorChange = (key) => (e) => {
    updateTheme({ [key]: e.target.value });
    setSaved(false);
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    resetTheme();
    setSaved(false);
  };

  return (
    <div className="page-content">
      <h2>Settings</h2>
      <p style={{ color: theme.textSecondary || '#666', marginBottom: '2rem' }}>Customize Inspection Butler for your company</p>

      {saved && <div className="alert alert-success">Settings saved successfully.</div>}

      <div className="settings-section">
        <h3>Company Branding</h3>
        <p style={{ color: theme.textSecondary || '#666', marginBottom: '1.5rem' }}>
          Make Inspection Butler look like your company. Changes apply instantly.
        </p>

        <div className="color-picker-row">
          <label>Button / Accent Color</label>
          <input type="color" value={theme.accentColor} onChange={handleColorChange('accentColor')} />
          <span style={{ color: theme.textSecondary || '#666', fontSize: '0.875rem' }}>{theme.accentColor}</span>
        </div>

        <div className="color-picker-row">
          <label>Background Color</label>
          <input type="color" value={theme.backgroundColor} onChange={handleColorChange('backgroundColor')} />
          <span style={{ color: theme.textSecondary || '#666', fontSize: '0.875rem' }}>{theme.backgroundColor}</span>
        </div>

        <div className="color-picker-row">
          <label>Text Color</label>
          <input type="color" value={theme.textColor} onChange={handleColorChange('textColor')} />
          <span style={{ color: theme.textSecondary || '#666', fontSize: '0.875rem' }}>{theme.textColor}</span>
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
          <button className="btn btn-primary" style={{ backgroundColor: theme.accentColor }} onClick={handleSave}>
            Save Changes
          </button>
          <button className="btn btn-secondary" style={{ color: theme.textColor }} onClick={handleReset}>
            Reset to Default
          </button>
        </div>
      </div>

      <div className="settings-section">
        <h3>Company Information</h3>
        <div className="form-group">
          <label style={{ color: theme.textColor }}>Company Name</label>
          <input
            className="form-input"
            value={theme.companyName || ''}
            onChange={(e) => updateTheme({ companyName: e.target.value })}
            placeholder="Your company name"
          />
        </div>
      </div>

      <div className="settings-section">
        <h3>Manager Unlock Code</h3>
        <p style={{ color: theme.textSecondary || '#666', marginBottom: '1rem' }}>
          When an inspection is submitted for AI review 6 or more times, it's locked. A manager with an unlock code can release it for another submission. Anyone authenticated can set a code here — only managers should actually set one.
        </p>

        {unlockStatus.hasCode ? (
          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#E8F5E9', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem', color: '#1B5E20' }}>
            You have a manager unlock code set{unlockStatus.label ? ' (' + unlockStatus.label + ')' : ''}.
            {unlockStatus.lastUsedAt && (
              <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                Last used: {new Date(unlockStatus.lastUsedAt).toLocaleString()}
              </div>
            )}
            <div style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>Enter a new code below to rotate it.</div>
          </div>
        ) : (
          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#FFF3E0', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem', color: '#E65100' }}>
            No code set. Set one below if you are a manager.
          </div>
        )}

        <div className="form-group">
          <Input
            label="New code (min 4 chars)"
            type="password"
            value={codeInput}
            onChange={e => setCodeInput(e.target.value)}
            placeholder="Enter new code"
            autoComplete="new-password"
          />
        </div>
        <div className="form-group">
          <Input
            label="Confirm code"
            type="password"
            value={codeConfirm}
            onChange={e => setCodeConfirm(e.target.value)}
            placeholder="Re-enter new code"
            autoComplete="new-password"
          />
        </div>
        <div className="form-group">
          <label style={{ color: theme.textColor }}>Label (optional)</label>
          <input
            className="form-input"
            value={codeLabel}
            onChange={e => setCodeLabel(e.target.value)}
            placeholder='e.g., "Office PIN"'
          />
        </div>
        {codeMsg && (
          <div className={codeMsg.type === 'success' ? 'alert alert-success' : 'alert alert-error'} style={{
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            backgroundColor: codeMsg.type === 'success' ? '#E8F5E9' : '#FFE5E5',
            color: codeMsg.type === 'success' ? '#1B5E20' : '#B00020',
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}>
            {codeMsg.text}
          </div>
        )}
        <button
          className="btn btn-primary"
          style={{ backgroundColor: theme.accentColor }}
          onClick={saveUnlockCode}
          disabled={codeSaving}
        >
          {codeSaving ? 'Saving...' : (unlockStatus.hasCode ? 'Rotate Code' : 'Set Code')}
        </button>
      </div>

      <div className="settings-section">
        <h3>Account</h3>
        <div className="card" style={{ borderColor: theme.borderColor || '#E5E5E5' }}>
          <p><strong>Name:</strong> {auth.user.firstName} {auth.user.lastName}</p>
          <p><strong>Email:</strong> {auth.user.email}</p>
          <p><strong>Role:</strong> {auth.user.role}</p>
        </div>
      </div>

      {pkSupported && (
        <div className="settings-section">
          <h3>Face ID / Sign-in</h3>
          <p style={{ color: theme.textSecondary || '#666', marginBottom: '1rem' }}>
            Sign in with Face ID (or your device's fingerprint / screen lock) instead of typing your password. This list is checked fresh every time you open this page.
          </p>
          <div className="card" style={{ borderColor: theme.borderColor || '#E5E5E5' }}>
            {pkListLoading ? (
              <p style={{ color: theme.textSecondary || '#666', margin: 0 }}>Checking…</p>
            ) : pkDevices.length === 0 ? (
              <p style={{ color: theme.textSecondary || '#666', margin: 0 }}>No devices set up yet.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {pkDevices.map((d) => {
                  const label = d.deviceName || d.deviceType || 'This device';
                  const added = fmtPkDate(d.createdAt);
                  return (
                    <li key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.5rem 0', borderBottom: '1px solid ' + (theme.borderColor || '#E5E5E5') }}>
                      <span>
                        <strong>{label}</strong>
                        {added && <span style={{ color: theme.textSecondary || '#666', fontSize: 'var(--text-caption)' }}>{' — added ' + added}</span>}
                      </span>
                      <Button variant="secondary" size="sm" disabled={pkBusy} onClick={() => handleRemovePasskey(d.id, label)}>Remove</Button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div style={{ marginTop: '1rem' }}>
              <Button variant="primary" size="sm" loading={pkBusy} onClick={handleSetupPasskey}>
                {pkDevices.length === 0 ? 'Set up Face ID on this device' : 'Add this device'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="settings-section">
        <h3>Change Password</h3>
        <p style={{ color: theme.textSecondary || '#666', marginBottom: '1.5rem' }}>
          Update your account password. You will stay signed in on this device, but any other sessions will be signed out.
        </p>
        <form onSubmit={handleChangePassword} noValidate>
          <div style={{ marginBottom: 'var(--space-4)', maxWidth: 440 }}>
            <Input
              label="Current Password"
              type="password"
              size="lg"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              placeholder="Your current password"
              autoComplete="current-password"
            />
          </div>
          <div style={{ marginBottom: 'var(--space-4)', maxWidth: 440 }}>
            <Input
              label="New Password"
              type="password"
              size="lg"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              placeholder="Create a strong new password"
              autoComplete="new-password"
            />
            <PasswordStrength password={pwNew} />
            {pwCurrent && pwNew && !pwDifferent && (
              <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-caption)', color: 'var(--color-error)' }}>
                New password must be different from current password.
              </div>
            )}
          </div>
          <div style={{ marginBottom: 'var(--space-6)', maxWidth: 440 }}>
            <Input
              label="Confirm New Password"
              type="password"
              size="lg"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              placeholder="Re-enter the new password"
              autoComplete="new-password"
            />
            {pwConfirm.length > 0 && !pwMatches && (
              <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-caption)', color: 'var(--color-error)' }}>
                Passwords do not match.
              </div>
            )}
          </div>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={pwSaving}
            disabled={pwSaving || !pwCurrent || !pwNewValid || !pwMatches || !pwDifferent}
          >
            Change Password
          </Button>
        </form>
      </div>
    </div>
  );
}

export default SettingsPage;
